import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import emailjs from "@emailjs/browser";
import axios from "axios";
import {
  Calculator as CalcIcon,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Phone,
  Loader2,
  Home,
  Hammer,
  Square,
  Layers,
  ChefHat,
  TreePine,
  Wind as WindIcon,
  DoorClosed,
  Fence,
  Box,
  AlertCircle,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/Breadcrumbs";
import CalculatorSEOContent, {
  calculatorFaqs,
} from "../components/CalculatorSEOContent";
import { calculateEstimate } from "../data/calculatorEngine";
import { UNKNOWN_DEFAULTS } from "../data/calculatorRates";
import { businessInfo } from "../mock";
import { trackEvent, getCalcEntrySource, trackCalcEntry } from "../utils/analytics";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const usd = (v) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(v || 0);

// =============================================================
// DAMAGE TYPE DEFINITIONS
// =============================================================
const DAMAGE_TYPES = [
  { key: "roof", label: "Roof", icon: Home },
  { key: "interior", label: "Interior (drywall/floor/mold)", icon: Hammer },
  { key: "windows", label: "Windows", icon: Square },
  { key: "siding", label: "Siding / Exterior", icon: Layers },
  { key: "cabinets", label: "Cabinets / Countertops", icon: ChefHat },
  { key: "screenEnclosure", label: "Screen Enclosure / Pool Cage", icon: TreePine },
  { key: "ac", label: "A/C / HVAC", icon: WindIcon },
  { key: "garage", label: "Garage Door", icon: DoorClosed },
  { key: "fence", label: "Fence", icon: Fence },
  { key: "contents", label: "Contents / Personal Property", icon: Box },
];

// Reusable form bits
const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="text-slate-900 text-sm font-bold tracking-wider uppercase">
      {label}
    </span>
    {hint && <span className="block text-slate-500 text-xs mt-1">{hint}</span>}
    <div className="mt-2">{children}</div>
  </label>
);

const Input = (props) => (
  <input
    {...props}
    className="w-full bg-white border border-slate-300 focus:border-[#E11D26] focus:outline-none text-slate-900 placeholder:text-slate-400 px-4 py-3 text-base"
  />
);

const Select = ({ options, value, onChange, testid }) => (
  <select
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    data-testid={testid}
    className="w-full bg-white border border-slate-300 focus:border-[#E11D26] focus:outline-none text-slate-900 px-4 py-3 text-base appearance-none"
  >
    <option value="">Select…</option>
    {options.map((o) => (
      <option key={o.value} value={o.value}>
        {o.label}
      </option>
    ))}
  </select>
);

const Toggle = ({ checked, onChange, label, testid }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    data-testid={testid}
    className={`flex items-center gap-3 px-4 py-3 border transition-colors w-full text-left ${
      checked
        ? "bg-[#E11D26]/10 border-[#E11D26] text-slate-900"
        : "bg-white border-slate-300 text-slate-700 hover:border-slate-500"
    }`}
  >
    <span
      className={`h-5 w-5 border-2 flex items-center justify-center shrink-0 ${
        checked ? "bg-[#E11D26] border-[#E11D26]" : "border-slate-400"
      }`}
    >
      {checked && <CheckCircle2 className="h-3 w-3 text-white" />}
    </span>
    <span className="font-semibold tracking-wide uppercase text-sm">
      {label}
    </span>
  </button>
);

// Number input + "I don't know — use average" checkbox
// When checked: the input is disabled and the engine substitutes the
// UNKNOWN_DEFAULTS fallback for that field.
const NumberWithUnknown = ({
  label,
  hint,
  value,
  unknownChecked,
  avgValue,
  onChange,
  onUnknownChange,
  testid,
  unknownTestid,
  placeholder,
}) => (
  <div>
    <Field label={label} hint={hint}>
      <Input
        type="number"
        min={0}
        data-testid={testid}
        disabled={unknownChecked}
        value={unknownChecked ? "" : value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={
          unknownChecked ? `Using FL average: ${avgValue}` : placeholder
        }
      />
    </Field>
    <label
      className="mt-2 inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 text-xs cursor-pointer select-none"
      data-testid={unknownTestid}
    >
      <input
        type="checkbox"
        checked={!!unknownChecked}
        onChange={(e) => onUnknownChange(e.target.checked)}
        className="h-4 w-4 accent-[#E11D26]"
      />
      I don&apos;t know — use FL average ({avgValue})
    </label>
  </div>
);

// =============================================================
// SECTION COMPONENTS
// =============================================================
const RoofSection = ({ data, onChange }) => (
  <div className="grid sm:grid-cols-2 gap-6">
    <Field label="Roof material">
      <Select
        testid="roof-material"
        value={data.material}
        onChange={(v) => onChange({ ...data, material: v })}
        options={[
          { value: "asphalt", label: "Asphalt shingle" },
          { value: "tile", label: "Tile (concrete/clay)" },
          { value: "metal", label: "Metal" },
          { value: "flat", label: "Flat (TPO/Mod Bit)" },
        ]}
      />
    </Field>
    <NumberWithUnknown
      testid="roof-house-sqft"
      unknownTestid="roof-house-sqft-unknown"
      label="Square feet of house"
      hint="Total living area. We'll auto-calculate roof size. Full tear-off & replacement is included."
      placeholder="e.g. 2200"
      value={data.houseSqft}
      unknownChecked={data.houseSqftUnknown}
      avgValue={UNKNOWN_DEFAULTS.houseSqft}
      onChange={(v) => onChange({ ...data, houseSqft: v })}
      onUnknownChange={(c) =>
        onChange({ ...data, houseSqftUnknown: c, houseSqft: "" })
      }
    />
    <NumberWithUnknown
      testid="roof-gutter-feet"
      unknownTestid="roof-gutter-feet-unknown"
      label="Linear feet of gutter damaged"
      hint="Optional — leave blank if no gutter damage."
      placeholder="e.g. 120"
      value={data.gutterFeet}
      unknownChecked={data.gutterFeetUnknown}
      avgValue={UNKNOWN_DEFAULTS.gutterFeet}
      onChange={(v) => onChange({ ...data, gutterFeet: v })}
      onUnknownChange={(c) =>
        onChange({ ...data, gutterFeetUnknown: c, gutterFeet: "" })
      }
    />
  </div>
);

const InteriorSection = ({ data, onChange }) => (
  <div className="grid sm:grid-cols-2 gap-6">
    <Field label="Severity per room">
      <Select
        value={data.severity}
        onChange={(v) => onChange({ ...data, severity: v })}
        options={[
          { value: "minor", label: "Minor (paint, small drywall patch)" },
          { value: "moderate", label: "Moderate (drywall replace, repaint)" },
          { value: "major", label: "Major (full gut, drywall + floor + paint)" },
        ]}
      />
    </Field>
    <NumberWithUnknown
      label="Rooms affected"
      placeholder="e.g. 3"
      value={data.roomsAffected}
      unknownChecked={data.roomsAffectedUnknown}
      avgValue={UNKNOWN_DEFAULTS.roomsAffected}
      onChange={(v) => onChange({ ...data, roomsAffected: v })}
      onUnknownChange={(c) =>
        onChange({ ...data, roomsAffectedUnknown: c, roomsAffected: "" })
      }
    />
    <Field label="Flooring type (if affected)">
      <Select
        value={data.floorType}
        onChange={(v) => onChange({ ...data, floorType: v })}
        options={[
          { value: "carpet", label: "Carpet" },
          { value: "laminate", label: "Laminate" },
          { value: "vinyl", label: "Vinyl / LVP" },
          { value: "tile", label: "Tile" },
          { value: "hardwood", label: "Hardwood" },
        ]}
      />
    </Field>
    <NumberWithUnknown
      label="Flooring sqft affected"
      placeholder="e.g. 400"
      value={data.floorSqft}
      unknownChecked={data.floorSqftUnknown}
      avgValue={UNKNOWN_DEFAULTS.floorSqft}
      onChange={(v) => onChange({ ...data, floorSqft: v })}
      onUnknownChange={(c) =>
        onChange({ ...data, floorSqftUnknown: c, floorSqft: "" })
      }
    />
    <Field label="Mold remediation needed?">
      <Select
        value={data.moldLevel}
        onChange={(v) => onChange({ ...data, moldLevel: v })}
        options={[
          { value: "none", label: "No mold" },
          { value: "small", label: "Small (1 room contained)" },
          { value: "medium", label: "Medium (multiple rooms)" },
          { value: "large", label: "Large (whole-home / HVAC affected)" },
        ]}
      />
    </Field>
  </div>
);

const WindowsSection = ({ data, onChange }) => (
  <div className="grid sm:grid-cols-2 gap-6">
    <NumberWithUnknown
      label="How many windows damaged?"
      placeholder="e.g. 6"
      value={data.totalCount}
      unknownChecked={data.totalCountUnknown}
      avgValue={UNKNOWN_DEFAULTS.windowCount}
      onChange={(v) => onChange({ ...data, totalCount: v })}
      onUnknownChange={(c) =>
        onChange({ ...data, totalCountUnknown: c, totalCount: "" })
      }
    />
    <Field
      label="Type of most windows"
      hint="We'll calculate using this rate for the count above."
    >
      <Select
        value={data.dominantType}
        onChange={(v) => onChange({ ...data, dominantType: v })}
        options={[
          { value: "standard", label: "Standard (single-hung vinyl)" },
          { value: "impact", label: "Impact-rated" },
          { value: "largePicture", label: "Large / picture / slider" },
        ]}
      />
    </Field>
  </div>
);

const SidingSection = ({ data, onChange }) => (
  <div className="grid sm:grid-cols-2 gap-6">
    <Field label="Siding material">
      <Select
        value={data.material}
        onChange={(v) => onChange({ ...data, material: v })}
        options={[
          { value: "vinyl", label: "Vinyl" },
          { value: "hardie", label: "Hardie / Fiber cement" },
          { value: "stucco", label: "Stucco" },
          { value: "wood", label: "Wood" },
          { value: "brick", label: "Brick" },
        ]}
      />
    </Field>
    <NumberWithUnknown
      label="Sqft of siding damaged"
      hint="A whole wall is roughly 250-450 sqft."
      placeholder="e.g. 350"
      value={data.sqft}
      unknownChecked={data.sqftUnknown}
      avgValue={UNKNOWN_DEFAULTS.sidingSqft}
      onChange={(v) => onChange({ ...data, sqft: v })}
      onUnknownChange={(c) =>
        onChange({ ...data, sqftUnknown: c, sqft: "" })
      }
    />
  </div>
);

const CabinetsSection = ({ data, onChange }) => (
  <div className="grid sm:grid-cols-2 gap-6">
    <Field label="Cabinet grade">
      <Select
        value={data.grade}
        onChange={(v) => onChange({ ...data, grade: v })}
        options={[
          { value: "builderGrade", label: "Builder grade" },
          { value: "midGrade", label: "Mid grade" },
          { value: "custom", label: "Custom / high end" },
        ]}
      />
    </Field>
    <NumberWithUnknown
      label="Linear feet of cabinetry damaged"
      hint="Measure across all bases + uppers."
      placeholder="e.g. 18"
      value={data.linearFeet}
      unknownChecked={data.linearFeetUnknown}
      avgValue={UNKNOWN_DEFAULTS.cabinetLinearFeet}
      onChange={(v) => onChange({ ...data, linearFeet: v })}
      onUnknownChange={(c) =>
        onChange({ ...data, linearFeetUnknown: c, linearFeet: "" })
      }
    />
    <Field label="Countertop material (if damaged)">
      <Select
        value={data.counterMaterial}
        onChange={(v) => onChange({ ...data, counterMaterial: v })}
        options={[
          { value: "laminate", label: "Laminate" },
          { value: "granite", label: "Granite" },
          { value: "quartz", label: "Quartz" },
          { value: "marble", label: "Marble" },
        ]}
      />
    </Field>
    <NumberWithUnknown
      label="Countertop sqft damaged"
      placeholder="e.g. 35"
      value={data.counterSqft}
      unknownChecked={data.counterSqftUnknown}
      avgValue={UNKNOWN_DEFAULTS.counterSqft}
      onChange={(v) => onChange({ ...data, counterSqft: v })}
      onUnknownChange={(c) =>
        onChange({ ...data, counterSqftUnknown: c, counterSqft: "" })
      }
    />
  </div>
);

const ScreenEnclosureSection = ({ data, onChange }) => (
  <div className="grid sm:grid-cols-2 gap-6">
    <Field label="Scope of damage">
      <Select
        value={data.scope}
        onChange={(v) => onChange({ ...data, scope: v })}
        options={[
          { value: "rescreen", label: "Rescreen only (mesh)" },
          { value: "partialRebuild", label: "Partial rebuild (some structure)" },
          { value: "fullRebuild", label: "Full rebuild (new cage)" },
        ]}
      />
    </Field>
    <NumberWithUnknown
      label="Cage footprint sqft"
      hint="Length × width of enclosure. Typical FL pool cage 800-1800 sqft."
      placeholder="e.g. 1200"
      value={data.sqft}
      unknownChecked={data.sqftUnknown}
      avgValue={UNKNOWN_DEFAULTS.screenEnclosureSqft}
      onChange={(v) => onChange({ ...data, sqft: v })}
      onUnknownChange={(c) =>
        onChange({ ...data, sqftUnknown: c, sqft: "" })
      }
    />
    <NumberWithUnknown
      label="Number of doors damaged"
      placeholder="e.g. 1"
      value={data.doorsNeeded}
      unknownChecked={data.doorsNeededUnknown}
      avgValue={UNKNOWN_DEFAULTS.doorsNeeded}
      onChange={(v) => onChange({ ...data, doorsNeeded: v })}
      onUnknownChange={(c) =>
        onChange({ ...data, doorsNeededUnknown: c, doorsNeeded: "" })
      }
    />
  </div>
);

const ACSection = ({ data, onChange }) => (
  <div className="grid sm:grid-cols-2 gap-5">
    <Field label="Equipment damaged">
      <Select
        value={data.system}
        onChange={(v) => onChange({ ...data, system: v })}
        options={[
          { value: "none", label: "None" },
          { value: "airHandlerOnly", label: "Air handler only" },
          { value: "condenserOnly", label: "Condenser only" },
          { value: "fullSystemSmall", label: "Full system (up to 2.5 ton)" },
          { value: "fullSystemMedium", label: "Full system (3-4 ton)" },
          { value: "fullSystemLarge", label: "Full system (5+ ton / dual)" },
        ]}
      />
    </Field>
    <Field label="Ductwork affected?">
      <Select
        value={data.ductwork}
        onChange={(v) => onChange({ ...data, ductwork: v })}
        options={[
          { value: "none", label: "No" },
          { value: "partial", label: "Partial replacement" },
          { value: "whole", label: "Whole-home replacement" },
        ]}
      />
    </Field>
  </div>
);

const GarageSection = ({ data, onChange }) => (
  <div className="grid sm:grid-cols-2 gap-5">
    <Field label="Garage door type">
      <Select
        value={data.type}
        onChange={(v) => onChange({ ...data, type: v })}
        options={[
          { value: "singleStandard", label: "Single, standard" },
          { value: "singleImpact", label: "Single, impact-rated" },
          { value: "doubleStandard", label: "Double, standard" },
          { value: "doubleImpact", label: "Double, impact-rated" },
        ]}
      />
    </Field>
    <Toggle
      label="Opener / motor damaged"
      checked={!!data.openerDamaged}
      onChange={(v) => onChange({ ...data, openerDamaged: v })}
    />
  </div>
);

const FenceSection = ({ data, onChange }) => (
  <div className="grid sm:grid-cols-2 gap-6">
    <Field label="Fence material">
      <Select
        value={data.material}
        onChange={(v) => onChange({ ...data, material: v })}
        options={[
          { value: "chainLink", label: "Chain link" },
          { value: "wood", label: "Wood" },
          { value: "vinyl", label: "Vinyl / PVC" },
          { value: "aluminum", label: "Aluminum" },
        ]}
      />
    </Field>
    <NumberWithUnknown
      label="Linear feet damaged"
      placeholder="e.g. 100"
      value={data.linearFeet}
      unknownChecked={data.linearFeetUnknown}
      avgValue={UNKNOWN_DEFAULTS.fenceLinearFeet}
      onChange={(v) => onChange({ ...data, linearFeet: v })}
      onUnknownChange={(c) =>
        onChange({ ...data, linearFeetUnknown: c, linearFeet: "" })
      }
    />
    <NumberWithUnknown
      label="Gates damaged"
      placeholder="e.g. 1"
      value={data.gates}
      unknownChecked={data.gatesUnknown}
      avgValue={UNKNOWN_DEFAULTS.gates}
      onChange={(v) => onChange({ ...data, gates: v })}
      onUnknownChange={(c) =>
        onChange({ ...data, gatesUnknown: c, gates: "" })
      }
    />
  </div>
);

const ContentsSection = ({ data, onChange }) => (
  <div className="grid gap-5">
    <NumberWithUnknown
      label="Estimated value of damaged contents"
      hint="Furniture, electronics, clothing, appliances — your best estimate at replacement cost."
      placeholder="e.g. 12000"
      value={data.estimatedValue}
      unknownChecked={data.estimatedValueUnknown}
      avgValue={UNKNOWN_DEFAULTS.contentsValue}
      onChange={(v) => onChange({ ...data, estimatedValue: v })}
      onUnknownChange={(c) =>
        onChange({ ...data, estimatedValueUnknown: c, estimatedValue: "" })
      }
    />
    <p className="text-slate-500 text-xs leading-relaxed">
      Our average contents recovery is ~35% higher than the homeowner&apos;s
      first estimate, because we account for proper RCV, sales tax, and
      depreciation buyback the carrier often misses.
    </p>
  </div>
);

const SECTION_COMPONENTS = {
  roof: RoofSection,
  interior: InteriorSection,
  windows: WindowsSection,
  siding: SidingSection,
  cabinets: CabinetsSection,
  screenEnclosure: ScreenEnclosureSection,
  ac: ACSection,
  garage: GarageSection,
  fence: FenceSection,
  contents: ContentsSection,
};

// =============================================================
// MAIN PAGE
// =============================================================
const CalculatorPage = () => {
  const [step, setStep] = useState(0);
  const [selected, setSelected] = useState({});
  const [sectionData, setSectionData] = useState({});
  const [contact, setContact] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    website: "", // honeypot
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [formStartTime] = useState(Date.now());
  const startedRef = useRef(false);
  const formViewedRef = useRef(false);
  const submittedRef = useRef(false);
  const abandonedRef = useRef(false);

  // Fire calculator_started once on the very first interaction.
  const fireStarted = () => {
    if (!startedRef.current) {
      startedRef.current = true;
      trackEvent("calculator_started", { entry_source: getCalcEntrySource() });
    }
  };

  // Fire calculator_abandoned once if the user reached the contact form
  // but left without submitting (tab close or SPA navigation away).
  const fireAbandoned = () => {
    if (
      formViewedRef.current &&
      !submittedRef.current &&
      !abandonedRef.current
    ) {
      abandonedRef.current = true;
      trackEvent("calculator_abandoned", {
        last_step: "lead_form",
        entry_source: getCalcEntrySource(),
      });
    }
  };

  // Build the dynamic step list:
  // 0 = damage selection, 1..N = each selected damage type, N+1 = contact, N+2 = result
  const activeDamages = useMemo(
    () => DAMAGE_TYPES.filter((d) => selected[d.key]),
    [selected]
  );
  const totalSteps = 1 + activeDamages.length + 2; // selection + N + contact + result
  const contactStep = 1 + activeDamages.length;
  const resultStep = contactStep + 1;

  const estimate = useMemo(
    () => calculateEstimate({ selected, ...sectionData }),
    [selected, sectionData]
  );

  // Fire lead_form_view once when the contact form step is reached.
  useEffect(() => {
    if (step === contactStep && !formViewedRef.current) {
      formViewedRef.current = true;
      trackEvent("lead_form_view", { entry_source: getCalcEntrySource() });
    }
  }, [step, contactStep]);

  // Detect abandonment: tab/window close while on the contact form, and
  // SPA navigation away from the calculator (component unmount).
  useEffect(() => {
    const handleUnload = () => fireAbandoned();
    window.addEventListener("beforeunload", handleUnload);
    return () => {
      window.removeEventListener("beforeunload", handleUnload);
      fireAbandoned();
    };
  }, []);

  const updateSection = (key, val) => {
    setSectionData((prev) => ({ ...prev, [key]: val }));
  };

  const goNext = () => {
    if (step === 0 && activeDamages.length === 0) return;
    if (step === 0) {
      trackEvent("damage_selected", {
        damage_count: activeDamages.length,
        damage_types: activeDamages.map((d) => d.key).join(","),
        entry_source: getCalcEntrySource(),
      });
    }
    if (step < totalSteps - 1) setStep(step + 1);
    if (typeof window !== "undefined") window.scrollTo(0, 0);
  };
  const goBack = () => {
    if (step > 0) setStep(step - 1);
    if (typeof window !== "undefined") window.scrollTo(0, 0);
  };

  const submitLead = async () => {
    setSubmitError("");
    // Validation
    if (!contact.name || !contact.email || !contact.phone || !contact.address) {
      setSubmitError("Please fill in all contact fields.");
      return;
    }
    // Honeypot + time trap
    if (contact.website) return;
    if (Date.now() - formStartTime < 4000) {
      setSubmitError("Please take a moment to complete the form.");
      return;
    }

    setSubmitting(true);
    try {
      // 1) Save to backend (MongoDB)
      await axios.post(`${BACKEND}/api/calculator/lead`, {
        name: contact.name,
        email: contact.email,
        phone: contact.phone,
        address: contact.address,
        low: estimate.low,
        likely: estimate.likely,
        high: estimate.high,
        breakdown: estimate.breakdown,
        answers: { selected, ...sectionData },
        website: contact.website,
      });

      // 2) Send EmailJS notification using existing template variables
      const breakdownText = estimate.breakdown
        .map((b) => `${b.label}: ${usd(b.value)}`)
        .join("\n");
      const message = `CLAIM CALCULATOR LEAD
=====================================
Estimate Range: ${usd(estimate.low)} - ${usd(estimate.high)}
Likely Recovery: ${usd(estimate.likely)}

Address: ${contact.address}

Damage Breakdown (line items):
${breakdownText}
=====================================
This is a calculator lead — homeowner has seen the estimate and is expecting a follow-up call within 24 hours.`;

      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name: contact.name,
          from_email: contact.email,
          phone: contact.phone,
          subject: `[CALCULATOR LEAD] ${contact.name} — ~${usd(
            estimate.likely
          )} estimate`,
          message,
          reply_to: contact.email,
        },
        EMAILJS_PUBLIC_KEY
      );

      setSubmitted(true);
      submittedRef.current = true;
      const entrySource = getCalcEntrySource();
      trackEvent("estimate_generated", {
        low: estimate.low,
        likely: estimate.likely,
        high: estimate.high,
        damage_count: activeDamages.length,
        entry_source: entrySource,
      });
      trackEvent("lead_submitted", {
        likely: estimate.likely,
        damage_types: activeDamages.map((d) => d.key).join(","),
        entry_source: entrySource,
      });
      setStep(resultStep);
      if (typeof window !== "undefined") window.scrollTo(0, 0);
    } catch (err) {
      // Even if EmailJS fails, if backend succeeded the lead is captured
      console.error("Calculator submission error", err);
      setSubmitError(
        "Something went wrong. Please call us at " +
          businessInfo.phone +
          " — we already have your details if it went through."
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ============================================================
  // RENDER
  // ============================================================
  const renderProgressBar = () => {
    const pct = ((step + 1) / totalSteps) * 100;
    return (
      <div className="border-b border-white/10 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex justify-between text-[10px] sm:text-xs uppercase tracking-[0.25em] text-white/55 mb-2">
            <span>
              Step {step + 1} of {totalSteps}
            </span>
            <span>{Math.round(pct)}% Complete</span>
          </div>
          <div className="h-1.5 bg-white/10 overflow-hidden">
            <div
              className="h-full bg-[#E11D26] transition-all duration-300"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderStep = () => {
    // Step 0: Damage selection
    if (step === 0) {
      return (
        <div>
          <h2
            className="text-3xl sm:text-4xl font-black text-slate-900 uppercase leading-tight"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            Which Types Of Damage{" "}
            <span className="text-[#E11D26]">Did You Have?</span>
          </h2>
          <p className="mt-4 text-slate-600 max-w-2xl">
            Select every damage type that applies. We&apos;ll only ask you
            questions about what you select — skip anything you don&apos;t
            have.
          </p>
          <div className="mt-8 grid sm:grid-cols-2 gap-3">
            {DAMAGE_TYPES.map((d) => {
              const Icon = d.icon;
              const on = !!selected[d.key];
              return (
                <button
                  key={d.key}
                  type="button"
                  onClick={() => {
                    fireStarted();
                    setSelected((p) => ({ ...p, [d.key]: !p[d.key] }));
                  }}
                  data-testid={`damage-toggle-${d.key}`}
                  className={`flex items-center gap-3 p-4 border text-left transition-all ${
                    on
                      ? "bg-[#E11D26]/10 border-[#E11D26]"
                      : "bg-white border-slate-300 hover:border-slate-500"
                  }`}
                >
                  <span
                    className={`h-10 w-10 flex items-center justify-center border ${
                      on
                        ? "bg-[#E11D26] border-[#E11D26] text-white"
                        : "border-slate-300 text-slate-500"
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="text-slate-900 font-bold uppercase text-sm tracking-wider">
                    {d.label}
                  </span>
                </button>
              );
            })}
          </div>
          {activeDamages.length === 0 && (
            <p className="mt-6 text-slate-500 text-sm flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              Select at least one damage type to continue.
            </p>
          )}
        </div>
      );
    }

    // Steps 1..N: each selected damage type
    if (step >= 1 && step < contactStep) {
      const damage = activeDamages[step - 1];
      const SectionEl = SECTION_COMPONENTS[damage.key];
      return (
        <div>
          <div className="text-[#E11D26] text-xs font-bold tracking-[0.3em] uppercase mb-3">
            Damage Section {step} of {activeDamages.length}
          </div>
          <h2
            className="text-3xl sm:text-4xl font-black text-slate-900 uppercase leading-tight"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            {damage.label}
          </h2>
          <p className="mt-3 text-slate-500 text-sm">
            Answer what you know — leave fields blank if you&apos;re unsure.
          </p>
          <div className="mt-8">
            <SectionEl
              data={sectionData[damage.key] || {}}
              onChange={(v) => updateSection(damage.key, v)}
            />
          </div>
        </div>
      );
    }

    // Contact step
    if (step === contactStep) {
      return (
        <div>
          <h2
            className="text-3xl sm:text-4xl font-black text-slate-900 uppercase leading-tight"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            Your Estimate Is <span className="text-[#E11D26]">Ready</span>.
          </h2>
          <p className="mt-4 text-slate-600 max-w-2xl text-base sm:text-lg">
            Tell us where to send it. We&apos;ll also reach out within 24
            hours to walk through your numbers and discuss next steps — no
            obligation, no upfront cost.
          </p>
          <div className="mt-8 grid sm:grid-cols-2 gap-5">
            <Field label="Full name">
              <Input
                type="text"
                data-testid="contact-name"
                value={contact.name}
                onChange={(e) =>
                  setContact({ ...contact, name: e.target.value })
                }
                placeholder="John Doe"
              />
            </Field>
            <Field label="Phone">
              <Input
                type="tel"
                data-testid="contact-phone"
                value={contact.phone}
                onChange={(e) =>
                  setContact({ ...contact, phone: e.target.value })
                }
                placeholder="(813) 555-0100"
              />
            </Field>
            <Field label="Email">
              <Input
                type="email"
                data-testid="contact-email"
                value={contact.email}
                onChange={(e) =>
                  setContact({ ...contact, email: e.target.value })
                }
                placeholder="you@email.com"
              />
            </Field>
            <Field label="Property address">
              <Input
                type="text"
                data-testid="contact-address"
                value={contact.address}
                onChange={(e) =>
                  setContact({ ...contact, address: e.target.value })
                }
                placeholder="123 Bayshore Blvd, Tampa, FL"
              />
            </Field>
          </div>
          {/* Honeypot */}
          <div
            style={{
              position: "absolute",
              left: "-9999px",
              opacity: 0,
              pointerEvents: "none",
            }}
            aria-hidden="true"
          >
            <label htmlFor="website-calc">Website</label>
            <input
              id="website-calc"
              tabIndex={-1}
              autoComplete="off"
              value={contact.website}
              onChange={(e) =>
                setContact({ ...contact, website: e.target.value })
              }
            />
          </div>
          {submitError && (
            <div
              className="mt-5 bg-[#E11D26]/10 border border-[#E11D26] text-slate-900 px-4 py-3 text-sm"
              data-testid="calc-submit-error"
            >
              {submitError}
            </div>
          )}
          <p className="mt-5 text-slate-500 text-xs">
            We never share or sell your information. By submitting you agree
            we may contact you about your claim.
          </p>
        </div>
      );
    }

    // Result step
    return (
      <div data-testid="calc-result">
        <div className="text-[#E11D26] text-xs font-bold tracking-[0.3em] uppercase mb-3">
          Your Estimated Claim Value
        </div>
        <h2
          className="text-3xl sm:text-5xl font-black text-slate-900 uppercase leading-tight"
          style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
        >
          {usd(estimate.low)} <span className="text-slate-400">–</span>{" "}
          {usd(estimate.high)}
        </h2>
        <div className="mt-5 p-6 bg-[#E11D26]/10 border border-[#E11D26] inline-block">
          <div className="text-slate-700 text-xs font-bold tracking-[0.3em] uppercase">
            Likely Recovery
          </div>
          <div
            className="mt-1 text-4xl sm:text-5xl font-black text-[#E11D26]"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
            data-testid="calc-likely"
          >
            {usd(estimate.likely)}
          </div>
        </div>

        <div className="mt-8 bg-slate-50 border border-slate-200 p-6">
          <div className="text-slate-600 text-xs font-bold tracking-[0.3em] uppercase mb-4">
            Line-Item Breakdown
          </div>
          <div className="divide-y divide-slate-200">
            {estimate.breakdown.length === 0 ? (
              <div className="py-3 text-slate-500">
                No damage line items selected.
              </div>
            ) : (
              estimate.breakdown.map((b) => (
                <div key={b.label} className="py-3 flex justify-between">
                  <span className="text-slate-700">{b.label}</span>
                  <span className="text-slate-900 font-bold">{usd(b.value)}</span>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="mt-8 p-6 bg-slate-50 border-l-4 border-[#E11D26]">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="h-6 w-6 text-[#E11D26] shrink-0 mt-0.5" />
            <div>
              <h3
                className="text-slate-900 text-xl font-black uppercase"
                style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
              >
                We&apos;ll Be Reaching Out
              </h3>
              <p className="mt-2 text-slate-700 leading-relaxed">
                Thanks, {contact.name.split(" ")[0] || "there"}. We&apos;ve
                got your details and a Claim Beast adjuster will contact you
                within <strong className="text-slate-900">24 hours</strong> to
                walk through your estimate and discuss next steps. Need to
                talk now? Call us directly:
              </p>
              <a
                href={`tel:${businessInfo.phoneRaw}`}
                className="mt-4 inline-flex items-center gap-2 bg-[#E11D26] hover:bg-black text-white px-6 py-3 font-bold tracking-wider uppercase text-sm transition-colors"
                data-testid="calc-call-now"
              >
                <Phone className="h-4 w-4" />
                Call {businessInfo.phone}
              </a>
            </div>
          </div>
        </div>

        <p className="mt-6 text-slate-500 text-xs leading-relaxed">
          This estimate is calculated from current Florida market labor and
          material rates and represents a typical claim value range — not a
          guarantee of recovery. Actual recovery depends on policy terms,
          documentation, and carrier-specific factors. Florida law caps PA
          fees at 20% of recovery (10% in declared emergencies, year one). No
          upfront cost — we only get paid when you do.
        </p>
      </div>
    );
  };

  const showFooterNav = step < resultStep;

  // FAQPage JSON-LD schema — decode HTML entities so Google sees plain text
  const decodeEntities = (str) =>
    String(str)
      .replace(/&apos;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, "&");

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: calculatorFaqs.map(({ q, a }) => ({
      "@type": "Question",
      name: decodeEntities(q),
      acceptedAnswer: {
        "@type": "Answer",
        text: decodeEntities(a),
      },
    })),
  };

  return (
    <div className="bg-black text-white min-h-screen">
      <SEO
        title="Free Florida Claim Estimate Calculator — Hurricane, Roof, Water | Claim Beast"
        description="Free Florida insurance claim calculator. Estimate hurricane, roof, water, fire, and contents damage values in 60 seconds. Get a no-obligation review from a licensed Tampa Bay public adjuster."
        path="/claim-calculator"
        keywords="florida claim calculator, insurance claim estimate, hurricane damage calculator, roof damage estimate, free claim review tampa, public adjuster estimate"
        schemas={[faqSchema]}
      />
      <Header />
      <Breadcrumbs items={[{ name: "Claim Calculator" }]} />

      {/* Intro / Hero */}
      <section className="bg-[#0A0A0A] border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
          <div className="flex items-center gap-3 text-[#E11D26]">
            <CalcIcon className="h-6 w-6" />
            <span className="text-xs font-bold tracking-[0.3em] uppercase">
              Free Claim Calculator
            </span>
          </div>
          <h1
            className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-black text-white uppercase leading-[1.05]"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            See What Your Florida{" "}
            <span className="text-[#E11D26]">Claim Is Worth</span>.
          </h1>
          <p className="mt-5 text-white/75 leading-relaxed text-base sm:text-lg max-w-2xl">
            Answer a few quick questions about your property damage. We&apos;ll
            calculate an estimated claim value using current Florida market
            rates — and a licensed public adjuster will reach out to review
            your estimate, free of charge.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3" data-testid="popular-calculators">
            <span className="text-white/50 text-xs font-bold tracking-[0.25em] uppercase">
              Popular:
            </span>
            <Link
              to="/roof-claim-calculator"
              data-testid="popular-calc-roof"
              onClick={() => trackEvent("popular_calc_click", { calculator: "roof", entry_source: getCalcEntrySource() })}
              className="inline-flex items-center gap-2 border border-[#E11D26]/50 bg-[#E11D26]/10 hover:bg-[#E11D26] text-white px-4 py-2 text-xs font-bold tracking-wider uppercase transition-colors"
            >
              Roof Claim Calculator
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <Link
              to="/water-damage-claim-calculator"
              data-testid="popular-calc-water"
              onClick={() => trackEvent("popular_calc_click", { calculator: "water", entry_source: getCalcEntrySource() })}
              className="inline-flex items-center gap-2 border border-[#E11D26]/50 bg-[#E11D26]/10 hover:bg-[#E11D26] text-white px-4 py-2 text-xs font-bold tracking-wider uppercase transition-colors"
            >
              Water Damage Calculator
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {renderProgressBar()}

      {/* Wizard body */}
      <section className="py-12 bg-gradient-to-b from-[#0A0A0A] to-[#141414]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 relative">
          <div className="bg-white shadow-2xl border-t-4 border-[#E11D26] p-6 sm:p-10 lg:p-12 relative">
          {renderStep()}

          {showFooterNav && (
            <div className="mt-10 pt-6 border-t border-slate-200 flex justify-between items-center gap-3">
              <button
                type="button"
                onClick={goBack}
                disabled={step === 0}
                data-testid="calc-back"
                className="inline-flex items-center gap-2 px-5 py-3 border border-slate-300 text-slate-700 hover:border-slate-900 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed font-bold tracking-wider uppercase text-sm transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>

              {step < contactStep && (
                <button
                  type="button"
                  onClick={goNext}
                  disabled={step === 0 && activeDamages.length === 0}
                  data-testid="calc-next"
                  className="inline-flex items-center gap-2 bg-[#E11D26] hover:bg-black text-white px-7 py-3 font-bold tracking-wider uppercase text-sm transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  {step === 0 ? "Start" : "Next"}
                  <ArrowRight className="h-4 w-4" />
                </button>
              )}

              {step === contactStep && (
                <button
                  type="button"
                  onClick={submitLead}
                  disabled={submitting}
                  data-testid="calc-submit"
                  className="inline-flex items-center gap-2 bg-[#E11D26] hover:bg-black text-white px-7 py-3 font-bold tracking-wider uppercase text-sm transition-colors disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Calculating…
                    </>
                  ) : (
                    <>
                      Show My Estimate
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>
              )}
            </div>
          )}

          {step === resultStep && (
            <div className="mt-10 pt-6 border-t border-slate-200 flex flex-wrap gap-3">
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 px-5 py-3 border border-slate-300 text-slate-800 hover:border-slate-900 hover:text-slate-900 font-bold tracking-wider uppercase text-sm transition-colors"
              >
                Read Our Claim Guides
              </Link>
              <Link
                to="/public-adjuster-tampa"
                className="inline-flex items-center gap-2 px-5 py-3 border border-slate-300 text-slate-800 hover:border-slate-900 hover:text-slate-900 font-bold tracking-wider uppercase text-sm transition-colors"
              >
                Tampa Public Adjuster
              </Link>
            </div>
          )}
          </div>
        </div>
      </section>

      {/* Internal links to high-intent claim pages */}
      <section
        className="bg-black border-t border-white/10 py-14"
        data-testid="calc-related-links"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-[#E11D26] text-xs font-bold tracking-[0.3em] uppercase mb-3">
            Related Florida Claim Help
          </div>
          <h2
            className="text-2xl sm:text-3xl font-black text-white uppercase leading-tight"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            Already Filed? <span className="text-[#E11D26]">See How We Fight Back</span>
          </h2>
          <div className="mt-7 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {[
              { t: "Denied Insurance Claim", href: "/denied-insurance-claim-florida" },
              { t: "Underpaid Insurance Claim", href: "/underpaid-insurance-claim-florida" },
              { t: "Hail Damage Claims", href: "/hail-damage" },
              { t: "Water Damage Claims", href: "/water-damage" },
              { t: "Hurricane / Wind Damage", href: "/wind-damage" },
              { t: "Roof Claim Calculator", href: "/roof-claim-calculator" },
              { t: "Water Damage Claim Calculator", href: "/water-damage-claim-calculator" },
            ].map((l) => (
              <Link
                key={l.href}
                to={l.href}
                data-testid={`calc-related-${l.href.replace(/\//g, "")}`}
                className="group bg-[#0F0F0F] hover:bg-[#E11D26] transition-colors p-5 flex items-center justify-between gap-3"
              >
                <span className="text-white font-bold uppercase text-sm tracking-wide">
                  {l.t}
                </span>
                <ArrowRight className="h-4 w-4 text-[#E11D26] group-hover:text-white transition-colors shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SEO content (always renders below the wizard) */}
      <CalculatorSEOContent />

      <Footer />
    </div>
  );
};

export default CalculatorPage;
